import { useEffect, useMemo, useState } from 'react'
import type { RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi } from '../api'
import { clampOverlays } from '../hydrology'
import { clampOverlaysToRegion } from '../maps/overlayRegion'
import { useFrameRegion } from '../maps/useFrameRegion'
import { overlayTileUrl } from './tileUrl'
import { TILE_OVERLAYS, overlayFlags } from './overlayActions'

/**
 * Рендер оверлеев наложения на карте раздела: тайл-оверлеи (лёд/туман/облачность),
 * землетрясения (geojson). Луна — виджет, рисуется в JSX страницы по overlayFlags.
 *
 * Общий хук для «Карт» и «Аналитики»: оба раздела рисуют одни и те же оверлеи из
 * одного стора (ViewContext). Перенос эффекта из MapsPage без изменения поведения:
 * пересчёт на смену набора/даты, землетрясения не перезапрашиваются на пан/зум.
 */
export function useOverlays(
  mapRef: RefObject<MapCanvasHandle | null>,
  requested: string[],
  date: string,
): { earthquakeCount: number } {
  // Набор приезжает из адреса, а права — из каталога: `overlays=…,ice` в
  // кабинете без гидрологии положил бы лёд на карту, которой его не выдавали.
  // Тумблеры чистить не надо (они и так строятся по каталогу) — рисование надо.
  //
  // Через `useMemo`, потому что набор стоит в зависимостях эффекта ниже: новый
  // массив на каждый кадр перезапрашивал бы землетрясения без конца.
  const { data } = useQuery({ queryKey: ['cabinet', 'layers'], queryFn: () => cabinetApi.layers() })
  const catalog = data?.layers
  // И район кадра: включённый лёд над Аравийским морем гаснет, а вернувшись
  // домой, карта рисует его снова — выбор человека в адресе цел.
  const frameRegion = useFrameRegion()
  const overlays = useMemo(
    () => clampOverlaysToRegion(clampOverlays(requested, catalog ?? []), catalog ?? [], frameRegion),
    [requested, catalog, frameRegion])
  // Сколько точек реально пришло: легенду на карте показываем по наличию
  // событий, а не по нажатому тумблеру — пустая плашка обещала бы то, чего в
  // кадре нет.
  const [earthquakeCount, setEarthquakeCount] = useState(0)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    for (const id of TILE_OVERLAYS) {
      const url = overlayTileUrl(id, date)
      if (overlays.includes(id) && url) map.addOverlay(id, url)
      else map.removeOverlay(id)
    }
    const { earthquakes } = overlayFlags(overlays)
    if (earthquakes) {
      cabinetApi
        .earthquakes(map.getBBox(), date)
        .then(fc => {
          // setQuakes, а не setGeoJson: кроме эпицентра рисуется ЗОНА
          // воздействия — круг радиусом в километрах, тот же, что на картах для
          // телеграма. Размер круга и есть область влияния события.
          map.setQuakes(fc as GeoJSON.FeatureCollection)
          setEarthquakeCount(fc?.features?.length ?? 0)
        })
        .catch(() => setEarthquakeCount(0))
    } else {
      map.removeQuakes()
      setEarthquakeCount(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlays, date])

  return { earthquakeCount }
}
